/**
 * src/modules/_panel-sheet.ts — panel filtrów jako wysuwany arkusz na wąskim ekranie (S-160 MOB-1, 10.10.2026).
 *
 * Problem (audyt mobilny 10.10, Z-01/O-07/I-02/S-06): na ≤1024 px panele filtrów list (statusy zamówień,
 * zwroty, klienci, faktury, PrestaShop/Erli/eBay) mają w CSS `display:none`, a przełączniki ustawiały
 * `panel.style.display = ''`, co nie przebija reguły z media query. Na telefonie i tablecie nie dało się
 * wybrać statusu, grupy roboczej ani filtra.
 *
 * Rozwiązanie: na ≤1024 px przełącznik nie rusza układu, tylko nakłada panelowi klasę `sb-panel-arkusz`
 * (CSS w `src/styles/mobile.css` pokazuje go jako wysuwaną z lewej szufladę nad treścią) i dokłada tło.
 * Zamyka: stuknięcie w tło, Esc, wybór pozycji filtra (`.osp-item`), zmiana trasy, poszerzenie okna.
 * Na szerszych ekranach `przelaczPanelArkusz()` zwraca false i wywołujący robi to, co dotąd (laptop bez zmian).
 */

const ZAPYTANIE = '(max-width: 1024px)';
let _tlo: HTMLElement | null = null;

export function panelJakoArkusz(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(ZAPYTANIE).matches;
}

function _naKlawisz(ev: KeyboardEvent): void {
  if (ev.key !== 'Escape') return;
  if (!document.querySelector('.sb-panel-arkusz')) return;
  ev.stopPropagation();
  zamknijPanelArkusz();
}

function _naWybor(ev: Event): void {
  const cel = ev.target as Element | null;
  const panel = cel?.closest('.sb-panel-arkusz');
  if (!panel) return;
  // Wybór filtra = koniec zadania arkusza. Nagłówki sekcji (zwijanie), pola i przełączniki osadzone
  // w wierszu (np. „Pokaż tagi / stany produkcji” grupy roboczej, rozwijanie grupy) zostawiają arkusz otwarty.
  const wiersz = cel?.closest('.osp-item');
  if (!wiersz) return;
  const kontrolka = cel?.closest('[data-osp-widok], .osp-grupa-chev, button, input, select, textarea, a[href]');
  if (kontrolka && kontrolka !== wiersz && wiersz.contains(kontrolka)) return;
  setTimeout(zamknijPanelArkusz, 180);
}

export function zamknijPanelArkusz(): void {
  document.querySelectorAll('.sb-panel-arkusz').forEach((p) => p.classList.remove('sb-panel-arkusz'));
  if (_tlo) { _tlo.remove(); _tlo = null; }
  document.body.classList.remove('sb-panel-arkusz-otwarty');
  document.removeEventListener('keydown', _naKlawisz, true);
  document.removeEventListener('click', _naWybor, true);
}

/** Na wąskim ekranie otwiera/zamyka `panel` jako arkusz i zwraca true. Na szerokim nic nie robi i zwraca false. */
export function przelaczPanelArkusz(panel: HTMLElement | null): boolean {
  if (!panel || !panelJakoArkusz()) return false;
  if (panel.classList.contains('sb-panel-arkusz')) { zamknijPanelArkusz(); return true; }
  zamknijPanelArkusz();
  panel.classList.add('sb-panel-arkusz');
  panel.scrollTop = 0;
  _tlo = document.createElement('div');
  _tlo.className = 'sb-panel-arkusz-tlo';
  _tlo.setAttribute('aria-hidden', 'true');
  _tlo.addEventListener('click', zamknijPanelArkusz);
  document.body.appendChild(_tlo);
  document.body.classList.add('sb-panel-arkusz-otwarty');
  document.addEventListener('keydown', _naKlawisz, true);
  document.addEventListener('click', _naWybor, true);
  return true;
}

/**
 * Podpina (raz) przycisk „Pokaż/ukryj filtry” listy, która nie ma własnej obsługi (Erli, eBay — audyt 10.10, L-3:
 * przycisk był martwy także na laptopie). Laptop: chowa/pokazuje panel jak w PrestaShop (`panel-hidden`
 * na układzie). Telefon/tablet: arkusz.
 */
export function podepnijPrzelacznikPanelu(btnId: string, panelId: string, layoutSelector: string): void {
  const btn = document.getElementById(btnId) as (HTMLElement & { _sbPanelBound?: boolean }) | null;
  if (!btn || btn._sbPanelBound) return;
  btn._sbPanelBound = true;
  btn.addEventListener('click', () => {
    const panel = document.getElementById(panelId);
    if (przelaczPanelArkusz(panel)) return;
    const layout = document.querySelector(layoutSelector);
    const ukryj = !(layout && layout.classList.contains('panel-hidden'));
    if (panel) panel.style.display = ukryj ? 'none' : '';
    if (layout) layout.classList.toggle('panel-hidden', ukryj);
  });
}

if (typeof window !== 'undefined') {
  (window as any).zamknijPanelArkusz = zamknijPanelArkusz;
  try {
    const mq = window.matchMedia(ZAPYTANIE);
    const naZmiane = () => { if (!mq.matches) zamknijPanelArkusz(); };
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', naZmiane);
    window.addEventListener('popstate', zamknijPanelArkusz);
  } catch (_e) { /* środowisko bez matchMedia (testy) */ }
}
